import { LoyaltyCartState } from "../../services/redux/types/LoyaltyCartTypes";
import {
  LoyaltyOfferStatus,
  OrderCartV2Line,
  OrderCartV2Output,
} from "../../services/redux/types/OrderCartV2Types";
import { clearLoyaltyRedemption } from "../../services/redux/utils/loyaltyCartHelpers";

/** Line IDs selected for the active offer — result.selections first, then pre-calculate fallback. */
export function getOfferSelectionLineIds(
  loyalty: Pick<LoyaltyCartState, "redemption" | "result">
): string[] {
  const applied = loyalty.result?.appliedOffer;

  /*
   * The buy side of a guest-built offer, which the response never lists among
   * its selections. Those lines were assembled on the offer page and show as
   * one row with the reward, so removing the offer has to take them too —
   * otherwise the guest removes "the offer" and is left paying for the items
   * they only ever added as part of it.
   *
   * Only when the builder sent the triggers itself. An offer that leaves its
   * buy side to the server has it allocated onto lines the guest already had,
   * and those are not ours to delete.
   */
  const builderSentTriggers =
    applied?.selectionMode === "EXPLICIT" &&
    !!loyalty.redemption?.pendingTriggers?.length;

  const triggerLineIds = builderSentTriggers
    ? (applied?.triggers ?? []).map((trigger) => trigger.lineId)
    : [];

  const fromResult = applied?.selections;
  if (fromResult?.length) {
    return Array.from(
      new Set([...fromResult.map((s) => s.lineId), ...triggerLineIds])
    );
  }

  const fallback = new Set<string>();
  loyalty.redemption?.pendingSelections?.forEach((s) => fallback.add(s.lineId));
  loyalty.redemption?.addedLineIds?.forEach((id) => fallback.add(id));

  return Array.from(fallback);
}

/** Line IDs to drop on removeLoyaltyOffer / INVALID — same set as offer selections. */
export function getOfferRemovalLineIds(loyalty: LoyaltyCartState): Set<string> {
  return new Set(getOfferSelectionLineIds(loyalty));
}

/**
 * Applies cart-level offer status from calculate response.
 * INVALID clears redemption and strips offer selection lines.
 */
export function applyCartOfferResponseStatus(
  loyalty: LoyaltyCartState,
  result: OrderCartV2Output,
  orderLines: OrderCartV2Line[]
): OrderCartV2Line[] {
  if (result.appliedOffer?.status !== LoyaltyOfferStatus.INVALID) {
    return orderLines;
  }

  const lineIdsToRemove = getOfferRemovalLineIds({ ...loyalty, result });
  clearLoyaltyRedemption(loyalty);

  if (lineIdsToRemove.size === 0) {
    return orderLines;
  }

  return orderLines.filter(
    (line) => !line.lineId || !lineIdsToRemove.has(line.lineId)
  );
}
